import { useLocation } from 'react-router-dom'
import { ComponentWithPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { TwinTableLink } from '../../../../../baseComponents/TwinTable'
import BaseScreenTable from '../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import {  CustomRenderDate, CustomRenderString, EditRow } from '../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { getBasePath } from '../../../../../utils/globals/link'
import { VatSupportedModelType } from '@teinor/erp/types/company/accounting/vatSupported'
import { DisplayStatus } from '../../../../../baseComponents/Displays/DisplayStatus'
import { faCheckCircle, faPencil } from '@fortawesome/pro-light-svg-icons'
import { SupplierPayload } from '../../../../../utils/reducers/products/suppliers'
import { WorkingCenterPayload } from '../../../../../utils/reducers/company/workingCenters'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import NullComponent from '../../../../../baseComponents/NullComponent'
import { DisplayPriceGettingParams } from '../../../../../baseComponents/Displays/DisplayPrice'

interface VatSupportedProps extends ComponentWithPermissions { }

const VatSupported: React.FC<VatSupportedProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('vatSupported', 'IVA Soportado'),
        newText: '',
    }

    return (
        <BaseScreenTable TableComponent={TableVatSupportedConnect} CreateModalComponent={NullComponent} pageData={pageData} userPermissions={userPermissions} />
    )
}

type TableVatSupportedProps = TableComponentProps & ReduxVatSupportedConnector & {}

const TableVatSupported: React.FC<TableVatSupportedProps> = ({ setTableInstance, workingCenters, setOpened, suppliers }) => {
    const { t } = useTwinTranslation()
    const location = useLocation().pathname
    const columns: ColumnTableSchema<VatSupportedModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('nAssigned', 'Nº Asignado'),
            sortable: true,
            minWidth: 150,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue || allRowData.id} className={parameterValue ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('nAssigned', 'Nº Asignado'), }
            },
        },
        {
            id: 'SupplierId',
            dataKey: 'SupplierId',
            label: t('supplier', 'Proveedor'),
            customRender: (parameterValue) => <CustomRenderString value={suppliers?.all?.[parameterValue as any]?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: suppliers?.all || {}, label: t('supplier', 'Proveedor') }
            },
        },
        {
            id: 'BillingWorkingCenterId',
            dataKey: 'BillingWorkingCenterId',
            label: t('billedFor', 'Facturado por'),
            customRender: (parameterValue) => <CustomRenderString value={workingCenters?.[parameterValue as any]?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: workingCenters || {}, label: t('workingCenter', 'Centro de trabajo') }
            },
            minWidth: 135
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            sortable: true,
            customRender: (parameterValue) => <VatSupportedStatus value={parameterValue} />,
            minWidth: 135
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('date', 'Fecha'),
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || allRowData.createdAt} className={parameterValue ? '' : 'text-red-BA'} />,
            sortable: true,
        },
        {
            id: 'total_without_vat',
            dataKey: 'total_without_vat',
            label: t('totalWithoutVatS', 'B. imponible'),
            simpleLabel: t('totalWithoutVat', 'Base imponible'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'total_total',
            dataKey: 'total_total',
            label: t('total', 'Total'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[getBasePath(location, 'vatSupported'), ['$id']]} name='vatSupported' columns={columns} getDataFrom='/api/app/accounting/vatSupported/getAllVatSupporteds' onRowClick={(_id, rowData) => setOpened({
            type: 'edit',
            allRowData: rowData
        })} setTableInstance={setTableInstance} />
    )
}

interface VatSupportedStatusProps {
    value: string
}

const VatSupportedStatus: React.FC<VatSupportedStatusProps> = ({ value }) => {
    const parsedValue = parseInt(value)
    const { t } = useTwinTranslation()
    if (parsedValue === 0) {
        return <DisplayStatus icon={faPencil} colorText='text-red-BA' text={t('draft', 'Borrador')} />
    } else if (parsedValue === 1) {
        return <DisplayStatus icon={faCheckCircle} colorText='text-green-43' text={t('processed', 'Procesado')} />
    }
    return null
}
const tableVatSupportedDispatch = {
    setSuppliers: (payload: SupplierPayload) => ({ type: 'CHANGE_SUPPLIER', payload }),
    setWorkingCenters: (payload: WorkingCenterPayload) => ({ type: 'CHANGE_WORKINGCENTER', payload }),
}
const mapTableVatSupportedConnector = (state: AllReduxPayloads) => ({ suppliers: state.suppliers, workingCenters: state.workingCenters })
const tableVatSupportedConnector = connect(mapTableVatSupportedConnector, tableVatSupportedDispatch)
type ReduxVatSupportedConnector = ConnectedProps<typeof tableVatSupportedConnector>

const tableVatSupportedConnectLoading = withLoading(TableVatSupported, [{ fetchUrl: '/api/app/workingCenter/getAllWorkingCentersComplete', propName: 'workingCenters', setFunctionName: 'setWorkingCenters' }, { fetchUrl: '/api/app/supplier/getAllSuppliersComplete', propName: 'suppliers', setFunctionName: 'setSuppliers' }])

const TableVatSupportedConnect = tableVatSupportedConnector(tableVatSupportedConnectLoading)

export default VatSupported